"use client";

import {
	type ParkerContextValue,
	ParkerStateProvider,
	useParker,
} from "@offstreetparking/portal-flow/providers/parker";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { offstreetClient } from "~/services/offstreetApi";
import { useAreParkerAccountsRolledOut } from "./useAreParkerAccountsRolledOut";

export { useParker };
export type { ParkerContextValue };

export const ParkerProvider = ({ children }: { children: React.ReactNode }) => {
	const { isPending: isFeatureFlagsPending } = useFeatureFlags();
	const globalParkerFlag = useAreParkerAccountsRolledOut();
	const utils = offstreetClient.useUtils();

	const auth = useAuth({ ensureSignedIn: false });
	const isParkerSignedIn = !!auth.user?.externalId && !auth.loading;
	const {
		data: parker,
		isPending: isParkerPending,
		error,
		refetch: refetchParker,
	} = offstreetClient.parkers.getParkerSession.useQuery(undefined, {
		enabled: isParkerSignedIn,
	});
	const isLoading = (isParkerPending && isParkerSignedIn) || auth.loading || isFeatureFlagsPending;

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	useEffect(() => {
		if (!isLoading) {
			setHasLoadedOnce(true);
		}
	}, [isLoading]);
	const isInitialLoading = isLoading && !hasLoadedOnce;

	const refreshAuth = useCallback(async () => {
		await auth.refreshAuth();
		const result = await refetchParker();
		if (result.error) {
			throw result.error;
		}
		if (!result.data) {
			await utils.invalidate();
			throw new Error("No parker data returned");
		}
		return result.data;
	}, [auth, refetchParker, utils]);

	const value = useMemo((): ParkerContextValue => {
		if (auth.user) {
			return {
				parker: parker || {
					firstName: auth.user.firstName ?? "",
					lastName: auth.user.lastName ?? "",
					email: auth.user.email ?? "",
					defaultVehicle: null,
				},
				displayName: [auth.user.firstName, auth.user.lastName].filter(Boolean).join(" ") || auth.user.email,
				error: error ?? undefined,
				authState: isLoading ? "authenticating" : "authenticated",
				isLoading,
				isInitialLoading,
				isLoggedIn: true,
				needsParkerCreation: !parker,
				isConnectedParker: !!auth.user.externalId,
				globalParkerFlag,
				refreshAuth,
			};
		}
		return {
			parker: undefined,
			displayName: undefined,
			error: error ?? undefined,
			authState: isLoading ? "authenticating" : "unauthenticated",
			isLoading,
			isInitialLoading,
			isLoggedIn: false,
			isConnectedParker: false,
			needsParkerCreation: false,
			globalParkerFlag,
			refreshAuth,
		};
	}, [auth, parker, error, isLoading, isInitialLoading, globalParkerFlag, refreshAuth]);

	return <ParkerStateProvider value={value}>{children}</ParkerStateProvider>;
};
